import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, CheckCircle, XCircle, AlertTriangle, Clock, History, FileText, Shield, Eye } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { ContractStatusBadge, KycStatusBadge } from '../ui/ContractStatusBadge';
import { useAuthStore } from '../../stores/auth.store';
import { DocumentViewer } from './DocumentViewer';

interface VirtualAccountContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  virtualAccountName?: string;
  onSuccess?: () => void;
}

type TabType = 'status' | 'kyc-origin' | 'kyc-destination' | 'history' | 'documents';

interface ContractStatus {
  virtualAccountId: string;
  contractStatus: string;
  contractApprovedAt?: string;
  contractApprovedBy?: string;
  contractSuspendedAt?: string;
  contractSuspendedBy?: string;
  contractSuspensionReason?: string;
  contractTerminatedAt?: string;
  contractTerminatedBy?: string;
  contractTerminationReason?: string;
  kycOriginStatus: string;
  kycOriginApprovedAt?: string;
  kycOriginApprovedBy?: string;
  kycOriginRejectedAt?: string;
  kycOriginRejectedBy?: string;
  kycOriginRejectionReason?: string;
  kycDestinationStatus: string;
  kycDestinationApprovedAt?: string;
  kycDestinationApprovedBy?: string;
  kycDestinationRejectedAt?: string;
  kycDestinationRejectedBy?: string;
  kycDestinationRejectionReason?: string;
  canReceive: boolean;
  canSend: boolean;
  blockReasons: string[];
}

interface HistoryItem {
  id: string;
  changeType: string;
  previousValue?: string;
  newValue: string;
  reason?: string;
  changedBy: string;
  changedByName: string;
  changedAt: string;
}

export function VirtualAccountContractModal({
  isOpen,
  onClose,
  virtualAccountId,
  virtualAccountName,
  onSuccess,
}: VirtualAccountContractModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('status');
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  
  // Admin/SuperAdmin/Operator/Compliance podem ver KYC Destino e ações administrativas
  const isAdminUser = user?.isSuperAdmin || ['ADMIN', 'OPERATOR', 'COMPLIANCE'].includes(user?.type || '');

  // Buscar status do contrato
  const { data: contractStatus, isLoading } = useQuery<ContractStatus>({
    queryKey: ['virtual-account-contract', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(`/virtual-accounts/${virtualAccountId}/contract`);
      return response.data;
    },
    enabled: isOpen && !!virtualAccountId,
  });

  // Buscar histórico
  const { data: historyData } = useQuery<{ history: HistoryItem[]; total: number }>({
    queryKey: ['virtual-account-contract-history', virtualAccountId],
    queryFn: async () => {
      const response = await api.get(`/virtual-accounts/${virtualAccountId}/contract/history`);
      return response.data;
    },
    enabled: isOpen && !!virtualAccountId && activeTab === 'history',
  });

  // Mutation para atualizar status do contrato
  const updateContractStatus = useMutation({
    mutationFn: async ({ status, reason }: { status: string; reason?: string }) => {
      const response = await api.put(`/virtual-accounts/${virtualAccountId}/contract/status`, {
        status,
        reason,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['virtual-account-contract', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account-contract-history', virtualAccountId] });
      setReason('');
      onSuccess?.();
    },
  });

  // Mutation para atualizar KYC de origem
  const updateKycOrigin = useMutation({
    mutationFn: async ({ status, reason }: { status: string; reason?: string }) => {
      const response = await api.put(`/virtual-accounts/${virtualAccountId}/contract/kyc-origin`, {
        status,
        reason,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['virtual-account-contract', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account-contract-history', virtualAccountId] });
      setReason('');
      onSuccess?.();
    },
  });

  // Mutation para atualizar KYC de destino
  const updateKycDestination = useMutation({
    mutationFn: async ({ status, reason }: { status: string; reason?: string }) => {
      const response = await api.put(`/virtual-accounts/${virtualAccountId}/contract/kyc-destination`, {
        status,
        reason,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['virtual-account-contract', virtualAccountId] });
      queryClient.invalidateQueries({ queryKey: ['virtual-account-contract-history', virtualAccountId] });
      setReason('');
      onSuccess?.();
    },
  });

  if (!isOpen) return null;

  const allTabs = [
    { id: 'status' as const, label: 'Status', icon: FileText },
    { id: 'kyc-origin' as const, label: 'KYC Origem', icon: Shield },
    // KYC Destino só visível para Admin/SuperAdmin (auto-aprovado para clientes)
    ...(isAdminUser ? [{ id: 'kyc-destination' as const, label: 'KYC Destino', icon: Shield }] : []),
    { id: 'documents' as const, label: 'Documentos', icon: Eye },
    { id: 'history' as const, label: 'Histórico', icon: History },
  ];

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getChangeTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CONTRACT_STATUS: 'Status do Contrato',
      KYC_ORIGIN: 'KYC de Origem',
      KYC_DESTINATION: 'KYC de Destino',
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-surface-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-surface-700">
          <div>
            <h2 className="text-lg font-semibold text-white">Gerenciar Contrato</h2>
            {virtualAccountName && (
              <p className="text-sm text-surface-400">{virtualAccountName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-surface-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-surface-700">
          {allTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-primary-400 border-b-2 border-primary-400'
                  : 'text-surface-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400" />
            </div>
          ) : contractStatus ? (
            <>
              {/* Tab: Status do Contrato */}
              {activeTab === 'status' && (
                <div className="space-y-6">
                  {/* Status atual */}
                  <div className="bg-surface-800 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-surface-400 mb-3">Status Atual</h3>
                    <div className="flex items-center gap-4">
                      <ContractStatusBadge status={contractStatus.contractStatus} size="lg" />
                      {contractStatus.contractApprovedAt && (
                        <span className="text-sm text-surface-400">
                          Aprovado em {formatDate(contractStatus.contractApprovedAt)}
                        </span>
                      )}
                    </div>
                    {contractStatus.contractSuspensionReason && (
                      <p className="mt-2 text-sm text-warning-400">
                        Motivo da suspensão: {contractStatus.contractSuspensionReason}
                      </p>
                    )}
                    {contractStatus.contractTerminationReason && (
                      <p className="mt-2 text-sm text-error-400">
                        Motivo do encerramento: {contractStatus.contractTerminationReason}
                      </p>
                    )}
                  </div>

                  {/* Bloqueios */}
                  {contractStatus.blockReasons.length > 0 && (
                    <div className="bg-error-900/20 border border-error-500/30 rounded-lg p-4">
                      <h3 className="text-sm font-medium text-error-400 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Operações Bloqueadas
                      </h3>
                      <ul className="space-y-1">
                        {contractStatus.blockReasons.map((reason, index) => (
                          <li key={index} className="text-sm text-error-300">• {reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Permissões */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`rounded-lg p-4 ${contractStatus.canReceive ? 'bg-success-900/20 border border-success-500/30' : 'bg-surface-800'}`}>
                      <div className="flex items-center gap-2">
                        {contractStatus.canReceive ? (
                          <CheckCircle className="w-5 h-5 text-success-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-surface-500" />
                        )}
                        <span className={contractStatus.canReceive ? 'text-success-400' : 'text-surface-500'}>
                          Pode Receber
                        </span>
                      </div>
                    </div>
                    <div className={`rounded-lg p-4 ${contractStatus.canSend ? 'bg-success-900/20 border border-success-500/30' : 'bg-surface-800'}`}>
                      <div className="flex items-center gap-2">
                        {contractStatus.canSend ? (
                          <CheckCircle className="w-5 h-5 text-success-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-surface-500" />
                        )}
                        <span className={contractStatus.canSend ? 'text-success-400' : 'text-surface-500'}>
                          Pode Enviar
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ações */}
                  {contractStatus.contractStatus !== 'TERMINATED' && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-surface-400">Ações</h3>
                      
                      {(contractStatus.contractStatus === 'SUSPENDED' || contractStatus.contractStatus === 'TERMINATED') && (
                        <textarea
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Motivo (obrigatório para suspensão/encerramento)"
                          className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          rows={2}
                        />
                      )}

                      <div className="flex gap-3">
                        {contractStatus.contractStatus === 'PENDING_APPROVAL' && (
                          <Button
                            onClick={() => updateContractStatus.mutate({ status: 'ACTIVE' })}
                            disabled={updateContractStatus.isPending}
                            variant="primary"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Ativar Contrato
                          </Button>
                        )}
                        
                        {contractStatus.contractStatus === 'ACTIVE' && (
                          <>
                            <Button
                              onClick={() => {
                                if (!reason) {
                                  alert('Informe o motivo da suspensão');
                                  return;
                                }
                                updateContractStatus.mutate({ status: 'SUSPENDED', reason });
                              }}
                              disabled={updateContractStatus.isPending}
                              variant="secondary"
                            >
                              <Clock className="w-4 h-4 mr-2" />
                              Suspender
                            </Button>
                            <Button
                              onClick={() => {
                                if (!reason) {
                                  alert('Informe o motivo do encerramento');
                                  return;
                                }
                                if (confirm('Tem certeza que deseja encerrar este contrato? Esta ação é irreversível.')) {
                                  updateContractStatus.mutate({ status: 'TERMINATED', reason });
                                }
                              }}
                              disabled={updateContractStatus.isPending}
                              variant="danger"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Encerrar
                            </Button>
                          </>
                        )}

                        {contractStatus.contractStatus === 'SUSPENDED' && (
                          <>
                            <Button
                              onClick={() => updateContractStatus.mutate({ status: 'ACTIVE' })}
                              disabled={updateContractStatus.isPending}
                              variant="primary"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Reativar
                            </Button>
                            <Button
                              onClick={() => {
                                if (!reason) {
                                  alert('Informe o motivo do encerramento');
                                  return;
                                }
                                if (confirm('Tem certeza que deseja encerrar este contrato? Esta ação é irreversível.')) {
                                  updateContractStatus.mutate({ status: 'TERMINATED', reason });
                                }
                              }}
                              disabled={updateContractStatus.isPending}
                              variant="danger"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Encerrar
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: KYC Origem */}
              {activeTab === 'kyc-origin' && (
                <div className="space-y-6">
                  <div className="bg-surface-800 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-surface-400 mb-3">Status do KYC de Origem</h3>
                    <p className="text-xs text-surface-500 mb-3">
                      O KYC de origem verifica a procedência dos recursos que entram na conta.
                    </p>
                    <div className="flex items-center gap-4">
                      <KycStatusBadge status={contractStatus.kycOriginStatus} type="origin" size="lg" showTooltip={false} />
                      {contractStatus.kycOriginApprovedAt && (
                        <span className="text-sm text-surface-400">
                          Aprovado em {formatDate(contractStatus.kycOriginApprovedAt)}
                        </span>
                      )}
                      {contractStatus.kycOriginRejectedAt && (
                        <span className="text-sm text-error-400">
                          Rejeitado em {formatDate(contractStatus.kycOriginRejectedAt)}
                        </span>
                      )}
                    </div>
                    {contractStatus.kycOriginRejectionReason && (
                      <p className="mt-2 text-sm text-error-400">
                        Motivo: {contractStatus.kycOriginRejectionReason}
                      </p>
                    )}
                  </div>

                  {/* Ações */}
                  {contractStatus.kycOriginStatus !== 'APPROVED' && (
                    <div className="space-y-4">
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Motivo (obrigatório para rejeição)"
                        className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows={2}
                      />
                      <div className="flex gap-3">
                        <Button
                          onClick={() => updateKycOrigin.mutate({ status: 'APPROVED' })}
                          disabled={updateKycOrigin.isPending}
                          variant="primary"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Aprovar KYC
                        </Button>
                        <Button
                          onClick={() => {
                            if (!reason) {
                              alert('Informe o motivo da rejeição');
                              return;
                            }
                            updateKycOrigin.mutate({ status: 'REJECTED', reason });
                          }}
                          disabled={updateKycOrigin.isPending}
                          variant="danger"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Rejeitar KYC
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: KYC Destino */}
              {activeTab === 'kyc-destination' && (
                <div className="space-y-6">
                  <div className="bg-surface-800 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-surface-400 mb-3">Status do KYC de Destino</h3>
                    <p className="text-xs text-surface-500 mb-3">
                      O KYC de destino verifica para onde os recursos podem ser enviados.
                    </p>
                    <div className="flex items-center gap-4">
                      <KycStatusBadge status={contractStatus.kycDestinationStatus} type="destination" size="lg" showTooltip={false} />
                      {contractStatus.kycDestinationApprovedAt && (
                        <span className="text-sm text-surface-400">
                          Aprovado em {formatDate(contractStatus.kycDestinationApprovedAt)}
                        </span>
                      )}
                      {contractStatus.kycDestinationRejectedAt && (
                        <span className="text-sm text-error-400">
                          Rejeitado em {formatDate(contractStatus.kycDestinationRejectedAt)}
                        </span>
                      )}
                    </div>
                    {contractStatus.kycDestinationRejectionReason && (
                      <p className="mt-2 text-sm text-error-400">
                        Motivo: {contractStatus.kycDestinationRejectionReason}
                      </p>
                    )}
                  </div>

                  {/* Ações */}
                  {contractStatus.kycDestinationStatus !== 'APPROVED' && (
                    <div className="space-y-4">
                      <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Motivo (obrigatório para rejeição)"
                        className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        rows={2}
                      />
                      <div className="flex gap-3">
                        <Button
                          onClick={() => updateKycDestination.mutate({ status: 'APPROVED' })}
                          disabled={updateKycDestination.isPending}
                          variant="primary"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Aprovar KYC
                        </Button>
                        <Button
                          onClick={() => {
                            if (!reason) {
                              alert('Informe o motivo da rejeição');
                              return;
                            }
                            updateKycDestination.mutate({ status: 'REJECTED', reason });
                          }}
                          disabled={updateKycDestination.isPending}
                          variant="danger"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Rejeitar KYC
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Documentos */}
              {activeTab === 'documents' && (
                <DocumentsTab virtualAccountId={virtualAccountId} isAdminUser={isAdminUser} />
              )}

              {/* Tab: Histórico */}
              {activeTab === 'history' && (
                <div className="space-y-4">
                  {historyData?.history && historyData.history.length > 0 ? (
                    <div className="space-y-3">
                      {historyData.history.map((item) => (
                        <div key={item.id} className="bg-surface-800 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <span className="text-sm font-medium text-white">
                                {getChangeTypeLabel(item.changeType)}
                              </span>
                              <div className="flex items-center gap-2 mt-1">
                                {item.previousValue && (
                                  <>
                                    <span className="text-xs text-surface-500">{item.previousValue}</span>
                                    <span className="text-xs text-surface-600">→</span>
                                  </>
                                )}
                                <span className="text-xs text-primary-400">{item.newValue}</span>
                              </div>
                              {item.reason && (
                                <p className="text-xs text-surface-400 mt-1">
                                  Motivo: {item.reason}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-surface-400">{item.changedByName}</p>
                              <p className="text-xs text-surface-500">{formatDate(item.changedAt)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-surface-500">
                      Nenhum histórico encontrado
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-surface-500">
              Erro ao carregar dados do contrato
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-surface-700">
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Componente de Documentos KYC
// ============================================

interface KycDocumentItem {
  id: string;
  documentTypeCode: string;
  documentTypeName: string;
  fileName: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  status: string;
  rejectionReason?: string | null;
  uploadedAt: string;
  verifiedAt?: string | null;
}

interface KycSubmissionData {
  id: string;
  kycType: string;
  status: string;
  documentsCount: number;
  documents: KycDocumentItem[];
  submittedAt: string;
}

interface KycDetailsData {
  virtualAccount: {
    id: string;
    userName: string;
    userEmail: string;
    userDocument: string;
    status: string;
    contractStatus: string;
  };
  origin?: KycSubmissionData;
  destination?: KycSubmissionData;
}

function DocumentsTab({ virtualAccountId, isAdminUser }: { virtualAccountId: string; isAdminUser: boolean }) {
  const [viewingDoc, setViewingDoc] = useState<{ url: string; name: string; mimeType: string } | null>(null);
  const { data: docsData, isLoading, error } = useQuery<KycDetailsData>({
    queryKey: ['kyc-documents', virtualAccountId, isAdminUser],
    queryFn: async () => {
      if (isAdminUser) {
        // Admin: GET /admin/kyc/:virtualAccountId (retorna detalhes com documentos)
        const response = await api.get(`/admin/kyc/${virtualAccountId}`);
        return response.data;
      } else {
        // Cliente: GET /kyc/my-documents/:virtualAccountId
        const response = await api.get(`/kyc/my-documents/${virtualAccountId}`);
        return response.data;
      }
    },
  });

  const handleViewDocument = async (docId: string, originalName: string, mimeType: string) => {
    try {
      const response = await api.get(
        isAdminUser
          ? `/admin/kyc/${virtualAccountId}/documents/${docId}/file`
          : `/kyc/my-documents/${virtualAccountId}/${docId}/file`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      setViewingDoc({ url, name: originalName, mimeType: response.headers['content-type'] || mimeType });
    } catch {
      alert('Erro ao abrir documento');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      UPLOADED: 'bg-blue-900/30 text-blue-400',
      VERIFIED: 'bg-success-900/30 text-success-400',
      REJECTED: 'bg-error-900/30 text-error-400',
      PENDING_REVIEW: 'bg-yellow-900/30 text-yellow-400',
    };
    const labels: Record<string, string> = {
      UPLOADED: 'Enviado',
      VERIFIED: 'Verificado',
      REJECTED: 'Rejeitado',
      PENDING_REVIEW: 'Em Análise',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs ${styles[status] || 'bg-surface-700 text-surface-400'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getDocIcon = (mimeType: string) => {
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.includes('pdf')) return '📄';
    return '📎';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-surface-500">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-yellow-500 opacity-50" />
        <p>Erro ao carregar documentos</p>
        <p className="text-xs mt-1">Verifique se o KYC foi submetido para esta conta</p>
      </div>
    );
  }

  const allDocuments = [
    ...(docsData?.origin?.documents || []).map(d => ({ ...d, kycType: 'Origem' as const, kycStatus: docsData?.origin?.status })),
    ...(docsData?.destination?.documents || []).map(d => ({ ...d, kycType: 'Destino' as const, kycStatus: docsData?.destination?.status })),
  ];

  if (allDocuments.length === 0) {
    return (
      <div className="text-center py-8 text-surface-500">
        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Nenhum documento encontrado</p>
        <p className="text-xs mt-1">Os documentos aparecerão aqui após a submissão do KYC pelo cliente</p>
      </div>
    );
  }

  // Agrupar por tipo de KYC
  const originDocs = allDocuments.filter(d => d.kycType === 'Origem');
  const destDocs = allDocuments.filter(d => d.kycType === 'Destino');

  const renderDocGroup = (title: string, docs: typeof allDocuments, status?: string) => {
    if (docs.length === 0) return null;
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-surface-300">{title}</h4>
          {status && getStatusBadge(status)}
        </div>
        {docs.map((doc) => (
          <div key={doc.id} className="bg-surface-800 rounded-lg p-4 flex items-center justify-between hover:bg-surface-750 transition-colors">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-xl flex-shrink-0">{getDocIcon(doc.mimeType)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-white truncate">{doc.originalName}</span>
                  {getStatusBadge(doc.status)}
                </div>
                <div className="flex items-center gap-3 text-xs text-surface-500">
                  <span>{doc.documentTypeName || 'Documento'}</span>
                  <span>•</span>
                  <span>{formatFileSize(doc.fileSize)}</span>
                  {doc.uploadedAt && (
                    <>
                      <span>•</span>
                      <span>{new Date(doc.uploadedAt).toLocaleString('pt-BR')}</span>
                    </>
                  )}
                </div>
                {doc.rejectionReason && (
                  <p className="text-xs text-error-400 mt-1">Motivo: {doc.rejectionReason}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleViewDocument(doc.id, doc.originalName, doc.mimeType)}
              className="ml-3 p-2 hover:bg-surface-700 rounded-lg transition-colors flex-shrink-0"
              title="Visualizar documento"
            >
              <Eye className="w-4 h-4 text-primary-400" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {viewingDoc && (
        <DocumentViewer
          documentUrl={viewingDoc.url}
          documentName={viewingDoc.name}
          mimeType={viewingDoc.mimeType}
          onClose={() => {
            if (viewingDoc.url) URL.revokeObjectURL(viewingDoc.url);
            setViewingDoc(null);
          }}
        />
      )}
      <div className="space-y-4">
        <p className="text-xs text-surface-500">
          Documentos submetidos para verificação KYC desta conta virtual.
          Total: {allDocuments.length} documento(s).
        </p>
        {renderDocGroup('📥 KYC Origem', originDocs, docsData?.origin?.status)}
        {renderDocGroup('📤 KYC Destino', destDocs, docsData?.destination?.status)}
      </div>
    </>
  );
}